import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import SensorsIcon from '@mui/icons-material/Sensors';
import TimerIcon from '@mui/icons-material/Timer';
import { Stack, Typography } from '@mui/material';
import { TournamentTypes } from 'common';
import { format } from 'date-fns';
import { FC } from 'react';

import ToournamentSponsorDefaultLogo from 'targets/web/assets/images/tournament/tournament-sponsor-default-logo.png';

interface TournamentCardBodyHeaderProps {
  tournament: TournamentTypes.TournamentConfig;
}

export const TournamentCardBodyHeader: FC<TournamentCardBodyHeaderProps> = ({ tournament }) => {
  const sponsorImage = tournament.sponsors[0]?.logoUri;

  // Format the start and end date using date-fns
  let dateRange = '';
  try {
    const start = tournament.startDate ? new Date(tournament.startDate) : null;
    const end = tournament.endDate ? new Date(tournament.endDate) : null;
    if (start && end) {
      dateRange = `${format(start, 'MMM do, h:mm a')} - ${format(end, 'MMM do, h:mm a')}`;
    } else if (start) {
      dateRange = format(start, 'MMM do, h:mm a');
    } else if (end) {
      dateRange = format(end, 'MMM do, h:mm a');
    }
  } catch {
    dateRange = ''; // Fallback in case of error
  }

  const getTournamentStatus = () => {
    switch (tournament.status) {
      case TournamentTypes.TournamentStatus.LIVE:
        return (
          <Stack
            sx={{
              height: 44,
              width: 103,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              backgroundColor: 'primary.main',
              color: '#171717',
              borderRadius: 32,
            }}>
            <SensorsIcon sx={{ fontSize: 24 }} />
            <Typography sx={{ fontFamily: 'RawsonBlack', fontSize: '24px' }}>Live</Typography>
          </Stack>
        );
      case TournamentTypes.TournamentStatus.WAITING:
        return (
          <Stack
            sx={{
              height: 44,
              width: 103,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              backgroundColor: 'white',
              color: '#171717',
              borderRadius: 32,
            }}>
            <HourglassBottomIcon sx={{ fontSize: 24 }} />
            <Typography sx={{ fontFamily: 'RawsonBlack', fontSize: '24px' }}>Waiting</Typography>
          </Stack>
        );
      case TournamentTypes.TournamentStatus.ENDED:
        return (
          <Stack
            sx={{
              height: 44,
              width: 103,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              backgroundColor: 'white',
              color: '#171717',
              borderRadius: 32,
            }}>
            <TimerIcon sx={{ fontSize: 24 }} />
            <Typography sx={{ fontFamily: 'RawsonBlack', fontSize: '24px' }}>Ended</Typography>
          </Stack>
        );
      case TournamentTypes.TournamentStatus.SUBMITTED:
        return (
          <Stack
            sx={{
              height: 44,
              width: 103,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              backgroundColor: 'white',
              color: '#171717',
              borderRadius: 32,
            }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 24 }} />
            <Typography sx={{ fontFamily: 'RawsonBlack', fontSize: '24px' }}>Submitted</Typography>
          </Stack>
        );
      case TournamentTypes.TournamentStatus.FINALIZING:
        return (
          <Stack
            sx={{
              height: 44,
              p: 4,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              backgroundColor: 'black',
              color: 'white',
              borderRadius: 32,
              borderColor: 'white',
              borderWidth: 1,
            }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 24 }} />
            <Typography sx={{ fontFamily: 'RawsonBlack', fontSize: '24px' }}>Finalizing</Typography>
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <Stack sx={{ px: 6, gap: 4, height: 254, justifyContent: 'center' }}>
      <Stack flexDirection="row" gap={6} alignItems="center" justifyContent="space-between">
        <Stack sx={{ flexDirection: 'row', gap: 6 }} alignItems="center">
          <img width={72} src={sponsorImage || ToournamentSponsorDefaultLogo} alt="" />
          <Typography sx={{ fontFamily: 'RawsonBlack', fontSize: '48px' }}>
            {tournament.name.substring(0, 50)}
            {tournament.name.length > 50 ? '...' : ''}
          </Typography>
        </Stack>
        {getTournamentStatus()}
      </Stack>
      <Typography sx={{ fontFamily: 'RawsonSemiBold', fontSize: '30px' }}>{dateRange}</Typography>
    </Stack>
  );
};
